import { Controller } from "@hotwired/stimulus"
import { unaccent } from "../unaccent.js"

// Connects to data-controller="tag-search"
// Handles autocomplete tag searching for large datasets
export default class extends Controller {
  // Normalize string the same way the backend does for sortable_name
  normalizeForSearch(str) {
    return unaccent(str)
      .replace(/\s*&\s*/g, ' and ')
      .replace(/^(the|an?)\s+/i, '')
      .replace(/^[^a-z0-9]+/i, '')
      .toLowerCase()
      .trim()
      .normalize()
  }

  // Find where the normalized search appears in the original string
  findMatchInOriginal(original, normalizedSearch) {
    const originalNormalized = this.normalizeForSearch(original)
    const pos = originalNormalized.indexOf(normalizedSearch)

    if (pos < 0) return -1

    // Now we need to map the position in normalized string back to original
    // We'll do this by counting characters while normalizing
    let origPos = 0
    let normPos = 0
    const origLower = original.toLowerCase()

    // Skip to the matching position in normalized string
    while (normPos < pos && origPos < original.length) {
      const origChar = original[origPos]
      const normalizedChar = this.normalizeForSearch(origChar)

      if (normalizedChar.length > 0) {
        normPos += normalizedChar.length
      }
      origPos++
    }

    return origPos
  }
  static values = {
    table: String,
    searchParam: String,
    multiple: { type: Boolean, default: true },
    placeholder: String,
    itemId: String,    // for prefilling single items
    itemVal: String
  }

  connect() {
    this.last_timeout = null
    this.one_single_item = null
    this.last_search_value = null
    this.last_show_value = null
    this.last_dataset = null
    this.fetching = false
    this.lookup_cache = {}

    this.createInputField()
    this.createItemsDiv()

    // Prefill if data provided
    if (this.itemIdValue && this.itemValValue) {
      this.addTag(this.itemIdValue, this.itemValValue)
    }

    // Check the form
    const my_form = this.element.closest('form')
    if (my_form) {  // no idea why this wouldn't exist
      const prefill_data_raw = my_form.dataset.prefill
      if (prefill_data_raw) {
        const prefill_data = JSON.parse(prefill_data_raw)
        if (prefill_data['lookups']) {
          if (prefill_data['lookups'][this.searchParamValue]) {
            const ids = prefill_data['lookups'][this.searchParamValue]['ids']
            const vals = prefill_data['lookups'][this.searchParamValue]['vals']
            for (let i = 0; i < ids.length; i++) {
              this.addTag(ids[i], vals[i])
            }
          }
        }
      }
    }

    this.setupEventListeners()
  }

  createInputField() {
    this.inputField = document.createElement('input')
    this.inputField.classList.add('form-control')
    this.inputField.placeholder = this.placeholderValue
    this.element.appendChild(this.inputField)
  }

  createItemsDiv() {
    this.itemsDiv = document.createElement('div')
    this.itemsDiv.classList.add('items')
    this.element.appendChild(this.itemsDiv)
  }

  setupEventListeners() {
    // Focus handling
    this.inputField.addEventListener('focus', () => {
      if (this.inputField.value.length > 0) {
        this.performSearch()
      }
    })

    // Focus out handling
    this.element.addEventListener('focusout', (ev) => {
      if (!this.focusIsInHere(ev)) {
        setTimeout(() => this.deactivateItemsDiv(), 500)
      }
    })

    // Keyboard navigation in input field
    this.inputField.addEventListener('keydown', (ev) => {
      if (ev.isComposing || ev.keyCode === 229) return

      if (ev.key == 'ArrowDown') {
        this.itemsDiv.querySelector('button:first-child')?.focus()
        ev.stopPropagation()
        ev.preventDefault()
      } else if (ev.key == 'Enter') {
        if (!this.fetching && this.one_single_item) {
          let the_item = this.itemsDiv.firstChild
          this.addTag(the_item.dataset.itemId, the_item.dataset.itemVal)
        }
        ev.stopPropagation()
        ev.preventDefault()
      }
    })

    // Keyboard navigation in items div
    this.itemsDiv.addEventListener('keydown', (ev) => {
      if (ev.key == 'ArrowDown') {
        if (ev.target.nextSibling) {
          ev.target.nextSibling.focus()
          ev.stopPropagation()
          ev.preventDefault()
        }
      } else if (ev.key == 'ArrowUp') {
        if (ev.target.previousSibling) {
          ev.target.previousSibling.focus()
        } else {
          this.inputField.focus()
        }
        ev.stopPropagation()
        ev.preventDefault()
      } else if (ev.target.nodeName == 'BUTTON') {
        if (ev.key == 'Enter') {
          this.addTag(ev.target.dataset.itemId, ev.target.dataset.itemVal)
          ev.stopPropagation()
          ev.preventDefault()
        } else if (ev.key == 'Escape') {
          this.inputField.focus()
          this.inputField.select()
          ev.stopPropagation()
          ev.preventDefault()
        }
      }
    }, true)

    // Click handling in items div
    this.itemsDiv.addEventListener('click', (ev) => {
      if (ev.target.nodeName == 'BUTTON') {
        this.addTag(ev.target.dataset.itemId, ev.target.dataset.itemVal)
      } else if (ev.target.nodeName == 'EM') {
        let btn = ev.target.closest('button')
        this.addTag(btn.dataset.itemId, btn.dataset.itemVal)
      }
    })

    // Input handling with debounce
    this.inputField.addEventListener('input', () => {
      if (this.fetching) return

      if (this.last_timeout !== null) {
        clearTimeout(this.last_timeout)
        this.last_timeout = null
      }

      const search_value = this.normalizeSearch(this.inputField.value)

      if (this.last_show_value === search_value) {
        this.performSearch()
      } else if (this.last_search_value !== null &&
                 search_value.startsWith(this.last_search_value) &&
                 !this.last_dataset.partial) {
        this.performSearch()
      } else {
        this.last_timeout = setTimeout(() => this.performSearch(), 1000)
      }
    })

    // Tag removal
    this.element.addEventListener('click', (ev) => {
      if (ev.target.nodeName == 'SPAN' && ev.target.classList.contains('remove')) {
        let tag_div = ev.target.closest('div.tag')
        if (tag_div) {
          this.removeTag(tag_div)
        }
      }
    }, true)
  }

  normalizeSearch(value) {
    return value.trimStart().replaceAll(/\s+/g, ' ').toLowerCase()
  }

  focusIsInHere(ev) {
    return (ev.relatedTarget &&
            ((ev.relatedTarget.dataset.itemVal && ev.relatedTarget.dataset.itemId) ||
             ev.relatedTarget == this.inputField))
  }

  deactivateItemsDiv() {
    this.itemsDiv.classList.remove('active')
  }

  hideInputField() {
    this.inputField.style.display = 'none'
    this.deactivateItemsDiv()
  }

  showInputField() {
    this.inputField.style.display = 'block'
    this.inputField.focus()
  }

  addTag(id, val) {
    // Only add a tag if it's not there already
    if (this.element.querySelector(`div.tag[data-item-id="${id}"]`)) {
      alert("You've already chosen this tag")
    } else {
      let tag_div = this.makeTag(id, val)
      this.element.insertBefore(tag_div, this.inputField)
      this.inputField.value = ''
      this.itemsDiv.classList.remove('active')

      if (!this.multipleValue) {
        // For single select, hide input after selection
        this.hideInputField()
      } else {
        this.inputField.focus()
      }
    }
  }

  removeTag(tag_div) {
    this.element.removeChild(tag_div)
    if (!this.multipleValue && !this.element.querySelector('div.tag')) {
      this.showInputField()
    }
  }

  makeTag(id, val) {
    let tag_div = document.createElement('div')
    tag_div.classList.add('tag')
    tag_div.dataset.itemId = id
    tag_div.dataset.itemVal = val
    tag_div.title = val

    // Create hidden field for form submission
    let hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = `search[search_params][with_all][${this.searchParamValue}][]`
    hidden.value = id
    tag_div.appendChild(hidden)

    let hidden_id = document.createElement('input')
    hidden_id.type = 'hidden'
    hidden_id.name = `search[search_params][lookups][${this.searchParamValue}][ids][]`
    hidden_id.value = id
    tag_div.appendChild(hidden_id)

    let hidden_val = document.createElement('input')
    hidden_val.type = 'hidden'
    hidden_val.name = `search[search_params][lookups][${this.searchParamValue}][vals][]`
    hidden_val.value = val
    tag_div.appendChild(hidden_val)

    let val_txt = document.createTextNode(val)
    tag_div.appendChild(val_txt)

    let icon_span = document.createElement('span')
    icon_span.classList.add('bi', 'bi-x-circle-fill', 'remove')
    tag_div.appendChild(icon_span)

    return tag_div
  }

  async performSearch() {
    const search_value = this.normalizeSearch(this.inputField.value)

    this.last_timeout = null

    // Require at least two characters
    if (search_value.length < 2) return

    this.itemsDiv.style.width = '' + this.inputField.clientWidth + 'px'

    // Check if we can reuse cached results
    if (this.last_show_value === search_value) {
      this.itemsDiv.classList.add('active')
      return
    }

    if (this.last_search_value !== null &&
        search_value.startsWith(this.last_search_value) &&
        !this.last_dataset.partial) {
      this.showCompletions(search_value, this.last_dataset)
      return
    }

    // Check cache
    const cached_value = this.findInCache(search_value)
    if (cached_value) {
      this.last_search_value = search_value
      this.last_dataset = cached_value
      this.showCompletions(search_value, this.last_dataset)
      return
    }

    // Fetch from server
    this.fetching = true
    try {
      const response = await fetch(
        `/autocomplete/autocomplete?table=${this.tableValue}&str=${encodeURIComponent(search_value)}`
      )

      if (response.ok) {
        const data = await response.json()
        this.last_search_value = search_value
        this.last_dataset = data
        this.lookup_cache[search_value] = data
        this.showCompletions(search_value, data)
      } else {
        alert('Search error')
      }
    } catch (err) {
      console.error(err)
      alert("Cannot load autocomplete")
    } finally {
      this.fetching = false
    }
  }

  findInCache(val) {
    if (this.lookup_cache[val]) {
      return this.lookup_cache[val]
    }
    // Can also return a non-partial prefix
    for (let k of Object.keys(this.lookup_cache)) {
      if (!this.lookup_cache[k].partial && val.startsWith(k)) {
        return this.lookup_cache[k]
      }
    }
    return null
  }

  showCompletions(search_value, data) {
    search_value = search_value.toLowerCase()
    const normalized_search = this.normalizeForSearch(search_value)
    this.last_show_value = search_value
    let item_count = 0
    let last_btn = null
    let shown_items = []

    let existing_buttons = Array.from(this.itemsDiv.querySelectorAll('button'))

    // Merge exact_items and items, removing duplicates
    let all_items = data.exact_items || []
    if (data.items) {
      all_items = all_items.concat(
        data.items.filter(x => !all_items.find(y => x[0] === y[0]))
      )
    }

    for (let item of all_items) {
      const val = item[1]
      const normalized_val = this.normalizeForSearch(val)

      // Check if this item matches
      if (normalized_val.includes(normalized_search)) {
        const id = item[0]
        const existing_btn = existing_buttons.find(btn => btn.dataset.itemId == id)

        // Find the best match position in the original string
        // Try to find the search term (just unaccented) in the original (unaccented)
        const unaccented_val = unaccent(val.toLowerCase())
        const unaccented_search = unaccent(search_value)
        let pos = unaccented_val.indexOf(unaccented_search)

        // If we can't find it with just unaccent, the match must be due to
        // "the"/"an"/"a" removal or "&" replacement, so just show without highlighting
        let prefix, matched_part, suffix
        if (pos < 0) {
          prefix = val
          matched_part = ''
          suffix = ''
        } else {
          prefix = val.substring(0, pos)
          matched_part = val.substring(pos, pos + unaccented_search.length)
          suffix = val.substring(pos + unaccented_search.length)
        }

        if (existing_btn) {
          // Update existing button
          last_btn = existing_btn
          existing_btn.innerHTML = ''
          existing_btn.appendChild(document.createTextNode(prefix))

          const matched_em = document.createElement('em')
          matched_em.appendChild(document.createTextNode(matched_part))
          existing_btn.appendChild(matched_em)

          existing_btn.appendChild(document.createTextNode(suffix))
        } else {
          // Create new button
          const btn = document.createElement('button')
          btn.type = 'button'
          btn.dataset.itemId = id
          btn.dataset.itemVal = val

          btn.appendChild(document.createTextNode(prefix))

          const matched_em = document.createElement('em')
          matched_em.appendChild(document.createTextNode(matched_part))
          btn.appendChild(matched_em)

          btn.appendChild(document.createTextNode(suffix))

          if (last_btn) {
            this.itemsDiv.insertBefore(btn, last_btn.nextSibling)
          } else {
            this.itemsDiv.insertBefore(btn, existing_buttons[0])
          }
          last_btn = btn
        }

        shown_items.push(item)
        item_count++
      }
    }

    // Remove buttons that aren't in the result set
    for (let btn of existing_buttons) {
      if (!shown_items.find(item => item[0] == btn.dataset.itemId)) {
        this.itemsDiv.removeChild(btn)
      }
    }

    // Show "nothing found" message or set height
    if (item_count == 0) {
      const sorry_p = document.createElement('p')
      sorry_p.appendChild(document.createTextNode('Nothing found...'))
      this.itemsDiv.appendChild(sorry_p)
      this.itemsDiv.style.height = ''
    } else {
      if (item_count < 5) {
        this.itemsDiv.style.height = ''
      } else {
        this.itemsDiv.style.height = '' + (24 * 5) + 'px'
      }
      const sorry_p = this.itemsDiv.querySelector('p')
      if (sorry_p) {
        this.itemsDiv.removeChild(sorry_p)
      }
    }

    this.itemsDiv.classList.add('active')
    this.one_single_item = (item_count == 1)
  }

  // Public method to clear all tags (called by search form reset)
  clearTags() {
    this.element.querySelectorAll('.tag').forEach(tag => {
      this.element.removeChild(tag)
    })
    this.showInputField()
  }
}
